import {useState,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import {useData} from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const {FiArrowLeft,FiPlus,FiBuilding,FiSearch,FiTag,FiPause,FiPlay,FiX,FiBriefcase,FiCreditCard,FiFileText}=FiIcons;

const CompanyManagement=()=> {
  const navigate=useNavigate();
  const {companies=[],reports=[],users=[],invoices=[],updateCompany}=useData();
  const [searchTerm,setSearchTerm]=useState('');
  const [filterStatus,setFilterStatus]=useState('active');
  const [viewingCompany,setViewingCompany]=useState(null);
  const [isEditingProfile,setIsEditingProfile]=useState(false);
  const [editFormData,setEditFormData]=useState({});

  const safeCompanies=Array.isArray(companies) ? companies : [];
  
  const filteredCompanies=safeCompanies.filter(company=> {
    const name=company.name || '';
    const matchesSearch=name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter=filterStatus==='all' || (filterStatus==='active' && company.active) || (filterStatus==='inactive' && !company.active);
    return matchesSearch && matchesFilter;
  });

  const sortedCompanies=[...filteredCompanies].sort((a,b)=> {
    return new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0);
  });

  const getCompanyStats=(companyId)=> {
    const safeReports=Array.isArray(reports) ? reports : [];
    const safeUsers=Array.isArray(users) ? users : [];
    const companyInvoices=Array.isArray(invoices) ? invoices.filter(inv=> inv.company_id===companyId || inv.companyId===companyId) : [];
    const latestInvoice=companyInvoices.sort((a,b)=> new Date(b.date) - new Date(a.date))[0];
    
    return {
      reports: safeReports.filter(r=> r.companyId===companyId || r.company_id===companyId).length,
      users: safeUsers.filter(u=> u.companyId===companyId || u.company_id===companyId).length,
      paymentStatus: latestInvoice?.status || 'Ingen faktura'
    };
  };

  const handleOpenDetails=(company)=> {
    setViewingCompany(company);
    setIsEditingProfile(false);
    setEditFormData({
      name: company.name || '',
      orgNumber: company.org_number || company.orgNumber || company.raw_data?.orgNumber || '',
      contactName: company.contact_person || company.contactName || company.raw_data?.contactName || '',
      contactEmail: company.contact_email || company.contactEmail || company.email || '',
      contactPhone: company.contactPhone || company.raw_data?.contactPhone || '',
      subscription: company.subscription || 'standard',
      monthlyFee: company.monthly_fee || company.monthlyFee || 0,
      active: company.active,
      salesAgentId: company.salesAgentId || company.raw_data?.salesAgentId || '',
      paymentMethod: company.paymentMethod || company.raw_data?.paymentMethod || 'invoice',
      appliedPromoCode: company.appliedPromoCode || company.raw_data?.appliedPromoCode || null
    });
  };

  const handleToggleActive=async (e,company)=> {
    e.stopPropagation();
    const newStatus = !company.active;
    const actionText = newStatus ? "aktivere" : "sette som inaktiv";
    if (window.confirm(`Vil du ${actionText} denne bedriften?`)) {
      const result = await updateCompany(company.id, { active: newStatus });
      if (!result.success) alert("Kunne ikke oppdatere status: " + result.error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button onClick={()=> navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg">
                <SafeIcon icon={FiArrowLeft} />
              </button>
              <h1 className="text-xl font-bold text-gray-900">Bedriftshåndtering</h1>
            </div>
            <button onClick={()=> navigate('/company/register')} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2">
              <SafeIcon icon={FiPlus} /> Ny bedrift
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm p-4 mb-8 flex flex-col lg:flex-row gap-4 items-center">
          <div className="flex-1 relative w-full">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Søk etter bedrifter..." 
              value={searchTerm} 
              onChange={(e)=> setSearchTerm(e.target.value)} 
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" 
            />
          </div>
          <div className="flex bg-gray-100 p-1 rounded-lg">
            <button onClick={()=> setFilterStatus('active')} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${filterStatus==='active' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              Aktive
            </button>
            <button onClick={()=> setFilterStatus('inactive')} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${filterStatus==='inactive' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              Inaktive
            </button>
            <button onClick={()=> setFilterStatus('all')} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${filterStatus==='all' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              Alle
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedCompanies.map((company)=> {
            const stats=getCompanyStats(company.id);
            const promo=company.appliedPromoCode || company.raw_data?.appliedPromoCode;
            const sellerId=company.salesAgentId || company.raw_data?.salesAgentId;
            const phone=company.contactPhone || company.raw_data?.contactPhone || company.raw_data?.contact?.phone || '-';
            const regDate=new Date(company.createdAt || company.created_at).toLocaleDateString('no-NO');

            return (
              <motion.div 
                key={company.id} 
                initial={{opacity: 0}} 
                animate={{opacity: 1}} 
                onClick={()=> handleOpenDetails(company)} 
                className={`bg-white rounded-xl shadow-sm p-6 border transition-all cursor-pointer group flex flex-col ${company.active ? 'border-gray-100 hover:border-blue-300' : 'border-red-200 bg-red-50/10 grayscale opacity-80'}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${company.active ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                      <SafeIcon icon={FiBuilding} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 leading-tight">{company.name}</h3>
                      <p className="text-[10px] text-gray-400 font-mono">ID: {company.id}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${company.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {company.active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>
                </div>

                <div className="space-y-3 mb-6 flex-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400 font-bold uppercase text-[9px]">Pakke</span>
                    <span className="font-bold text-gray-700 capitalize">{company.subscription || 'Standard'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400 font-bold uppercase text-[9px]">Månedspris</span>
                    <span className="font-black text-blue-600">{company.monthly_fee || company.monthlyFee || 0} kr</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400 font-bold uppercase text-[9px]">Selger/Partner</span>
                    <span className="font-bold text-indigo-600">{sellerId || 'Direkte'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400 font-bold uppercase text-[9px]">Telefon</span>
                    <span className="font-bold text-gray-700">{phone}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400 font-bold uppercase text-[9px]">Registrert</span>
                    <span className="font-bold text-gray-700">{regDate}</span>
                  </div>
                  {promo && (
                    <div className="flex justify-between text-xs bg-orange-50 p-1.5 rounded border border-orange-100 mt-2">
                      <span className="text-orange-600 font-bold uppercase text-[9px] flex items-center gap-1">
                        <SafeIcon icon={FiTag} /> Rabatt
                      </span>
                      <span className="font-black text-orange-700 font-mono">{promo}</span>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-50 flex justify-between items-center mt-auto">
                  <div className="flex gap-2">
                    <button 
                      onClick={(e)=> handleToggleActive(e, company)} 
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-colors ${company.active ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}
                    >
                      <SafeIcon icon={company.active ? FiPause : FiPlay} /> {company.active ? 'Sett Inaktiv' : 'Aktiver igjen'}
                    </button>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] text-gray-400 uppercase font-bold">Betaling</p>
                    <p className={`text-[10px] font-black uppercase ${stats.paymentStatus==='paid' ? 'text-green-600' : 'text-orange-500'}`}>
                      {stats.paymentStatus}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        
        {sortedCompanies.length===0 && (
          <div className="text-center py-24 bg-white rounded-2xl border-2 border-dashed border-gray-200">
            <SafeIcon icon={FiBuilding} className="mx-auto text-4xl text-gray-200 mb-4" />
            <h3 className="text-lg font-bold text-gray-400">Ingen bedrifter funnet med status "{filterStatus}"</h3>
          </div>
        )}
      </div>

      <AnimatePresence>
        {viewingCompany && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={()=> setViewingCompany(null)}>
            <motion.div 
              initial={{scale: 0.9,opacity: 0}} 
              animate={{scale: 1,opacity: 1}} 
              exit={{scale: 0.9,opacity: 0}} 
              className="bg-white w-full max-w-3xl rounded-[32px] shadow-2xl overflow-hidden" 
              onClick={e=> e.stopPropagation()}
            >
              <div className="bg-blue-800 p-8 text-white flex justify-between items-start">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center backdrop-blur-md">
                    <SafeIcon icon={FiBuilding} size={40} />
                  </div>
                  <div>
                    <h2 className="text-3xl font-black">{viewingCompany.name}</h2>
                    <p className="text-blue-100 flex items-center gap-2 mt-1">
                      <SafeIcon icon={FiTag} /> Org.nr: {viewingCompany.org_number || viewingCompany.orgNumber || '-'}
                    </p>
                  </div>
                </div>
                <button onClick={()=> setViewingCompany(null)} className="p-2 hover:bg-white/20 rounded-full"><SafeIcon icon={FiX} size={24} /></button>
              </div>

              <div className="p-8 overflow-y-auto max-h-[70vh]">
                <div className="space-y-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest border-b pb-2">Kontaktinformasjon</h3>
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-4 rounded-2xl">
                          <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Kontaktperson</p>
                          <p className="font-bold text-gray-900">{editFormData.contactName || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-2xl">
                          <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">E-post</p>
                          <p className="font-bold text-gray-900">{editFormData.contactEmail || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-2xl">
                          <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Telefon</p>
                          <p className="font-bold text-gray-900">{editFormData.contactPhone || '-'}</p>
                        </div>
                        <div className="bg-indigo-50 p-4 rounded-2xl border border-indigo-100">
                          <p className="text-[10px] font-bold text-indigo-400 uppercase mb-1 flex items-center gap-1">
                            <SafeIcon icon={FiBriefcase} /> Selger / Partner ID
                          </p>
                          <p className="font-mono font-bold text-indigo-900">{editFormData.salesAgentId || 'Ingen selger/partner registrert'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest border-b pb-2">Økonomi & Betaling</h3>
                      <div className="bg-gradient-to-br from-blue-900 to-indigo-900 p-6 rounded-3xl space-y-4 text-white shadow-xl">
                        <div className="flex justify-between items-center border-b border-white/10 pb-3">
                          <span className="text-blue-300 text-[10px] font-bold uppercase">Betalingsmetode</span>
                          <span className="font-black flex items-center gap-2">
                            <SafeIcon icon={editFormData.paymentMethod==='card' ? FiCreditCard : FiFileText} /> 
                            {editFormData.paymentMethod==='card' ? 'Kort / Stripe' : 'Faktura (EHF)'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center border-b border-white/10 pb-3">
                          <span className="text-blue-300 text-[10px] font-bold uppercase">Gjeldende Status</span>
                          <span className={`font-black uppercase text-xs px-2 py-1 rounded ${getCompanyStats(viewingCompany.id).paymentStatus==='paid' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                            {getCompanyStats(viewingCompany.id).paymentStatus}
                          </span>
                        </div>
                        <div className="flex justify-between items-center border-b border-white/10 pb-3">
                          <span className="text-blue-300 text-[10px] font-bold uppercase">Rabattkode</span>
                          <span className="font-mono text-yellow-400 font-bold">{editFormData.appliedPromoCode || 'Ingen'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-blue-300 text-[10px] font-bold uppercase">Månedspris</span>
                          <span className="text-2xl font-black text-blue-400">{editFormData.monthlyFee} kr</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-8 bg-gray-50 -mx-8 -mb-8 border-t flex justify-end mt-8">
                  <button type="button" onClick={()=> setViewingCompany(null)} className="px-12 py-4 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-blue-800 transition-all shadow-lg">
                    Lukk Oversikt
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompanyManagement;